import AppState, { setShouldAllowPayment } from '@/states/app'

import Calendar from '@/models/calendar'
import { h } from 'vue'
import { PaymentService } from '../class/services/PaymentService'
import { PaymentProvider } from './constants'
import { formatDuration } from './datehelper'
import {
  getDurationOptionsFromCalendar,
  normalizeApiMinutes,
} from './durationOptionsValidation'

export function isString(a: any) {
  if (typeof a === 'string' || a instanceof String) {
    return true
  }

  return false
}

export function isNumber(a: any) {
  if (typeof a === 'number' || a instanceof Number) {
    return true
  }

  return false
}

export function sortOptions<T extends Record<string, any>>(
  options: T[],
  key: keyof T
): T[] {
  if (!options || options.length < 1) {
    return []
  }

  return options.sort((first, second) => {
    if (!first[key] || !second[key]) {
      return 0
    }

    if (isString(first[key]) && isString(second[key])) {
      if (first[key].toLowerCase() < second[key].toLowerCase()) {
        return -1
      }
      if (first[key].toLowerCase() > second[key].toLowerCase()) {
        return 1
      }
    } else if (isNumber(first[key]) && isNumber(second[key])) {
      if (first[key] < second[key]) {
        return -1
      }
      if (first[key] > second[key]) {
        return 1
      }
    }

    return 0
  })
}

export const copyToClipboard = (text: string | undefined) => {
  if (!text) {
    return
  }
  // console.log('copyToClipboard', text)
  AppState.parentApp?.then(parent => {
    parent.emit('copy-to-clipboard', text)
  })
}

export const openInNewTab = (url: string) => {
  window.open(url, '_blank')?.focus()
}

export const shouldShowLegacyLink = (key: string, calendar: Calendar) => {
  return key !== 'copy_scheduling_link' || calendar.slug
}

export const checkForValidPaymentProvider = (defaultPaymentProvider: any) => {
  if (!defaultPaymentProvider) {
    return false
  }
  switch (defaultPaymentProvider.provider) {
    case PaymentProvider.AUTHORIZE_NET:
      return !!(
        defaultPaymentProvider.providerConfig?.live?.merchantGatewayId ||
        defaultPaymentProvider.providerConfig?.test?.merchantGatewayId
      )
    case PaymentProvider.SQUARE:
    case PaymentProvider.NMI:
      return !!(
        defaultPaymentProvider.providerConfig?.live?.merchantGatewayId ||
        defaultPaymentProvider.providerConfig?.test?.merchantGatewayId
      )
    case PaymentProvider.STRIPE:
    case PaymentProvider.CUSTOM_PROVIDER:
      return !!(
        defaultPaymentProvider.providerConfig?.live?.publishableKey ||
        defaultPaymentProvider.providerConfig?.test?.publishableKey
      )
    case PaymentProvider.PAYPAL:
      return !!(
        defaultPaymentProvider.providerConfig?.live?.paypalPublishableKey ||
        defaultPaymentProvider.providerConfig?.test?.paypalPublishableKey
      )
    case PaymentProvider.ADYEN:
      return !!(
        defaultPaymentProvider.providerConfig?.live?.merchantAccountId ||
        defaultPaymentProvider.providerConfig?.test?.merchantAccountId
      )
    default:
      return false
  }
}

export const isPaymentAllowed = async (locationId: string) => {
  if (
    AppState.paymentProviderInfo &&
    AppState.shouldAllowPayment !== null &&
    AppState.shouldAllowPayment !== undefined
  ) {
    return
  }
  try {
    const { data: paymentProvider } =
      await PaymentService.getDefaultPaymentProvider(locationId)

    const calendarProviders = paymentProvider?.providers?.calendars ?? []
    const hasCalendarProviders = calendarProviders.length > 0

    const defaultProvider = paymentProvider?.defaultPaymentProvider
    const expressProvider = paymentProvider?.expressPaymentProvider

    let selectedProvider

    if (hasCalendarProviders) {
      selectedProvider =
        calendarProviders.find(p => p.provider !== PaymentProvider.PAYPAL) ??
        calendarProviders[0]
    } else if (defaultProvider?.provider) {
      selectedProvider = defaultProvider
    } else if (
      expressProvider?.providerConfig?.live?.paypalPublishableKey ||
      expressProvider?.providerConfig?.test?.paypalPublishableKey
    ) {
      selectedProvider = expressProvider
    }

    AppState.paymentProviderInfo = selectedProvider
    AppState.defaultPaymentProvider = selectedProvider?.provider ?? null

    const paypalSource = hasCalendarProviders
      ? calendarProviders.find(p => p.provider === PaymentProvider.PAYPAL)
      : expressProvider

    const paypalConfig = paypalSource?.providerConfig
    AppState.isPayPalEnabled = Boolean(
      paypalConfig?.test?.merchantClientId ||
      paypalConfig?.live?.merchantClientId
    )

    const shouldAllowPayment = checkForValidPaymentProvider(selectedProvider)
    setShouldAllowPayment(shouldAllowPayment)
  } catch {
    AppState.defaultPaymentProvider = null
  }
}

export const generateSlug = () => {
  let slug = ''
  const characters = 'abcdefghijklmnopqrstuvwxyz0123456789'
  const charactersLength = characters.length
  for (let i = 0; i < 6; i++) {
    slug += characters.charAt(Math.floor(Math.random() * charactersLength))
  }
  return slug
}
export const getTooltip = (text: string) => {
  const words = text.split(' ')
  const avgCharPx = 7
  const maxLineWidth = 320
  let lineWidth = 0
  const formattedText = words
    .map(word => {
      if (word.includes('\n')) {
        const parts = word.split('\n')
        lineWidth = (parts[parts.length - 1]?.length || 0) * avgCharPx
        return word
      }

      // Account for space width (except at start of line)
      const spaceWidth = lineWidth > 0 ? avgCharPx : 0
      const wordWidth = word.length * avgCharPx

      // Check if adding this word would exceed max width
      if (lineWidth + spaceWidth + wordWidth >= maxLineWidth) {
        lineWidth = wordWidth
        return '\n' + word
      }

      lineWidth += spaceWidth + wordWidth
      return word
    })
    .join(' ')

  const splitText = formattedText
    .split('\n')
    .map((line, index) => [index > 0 && h('br'), line])

  return {
    text: () => h('div', {}, splitText),
  }
}

export const getDefaultTeamMember = (userId: string) => {
  return {
    userId,
    priority: 0.5,
    isZoomAdded: 'false',
    zoomOauthId: '',
    meetingLocation: '',
    selected: true,
  }
}

export const getTimeSpanOptions = (
  maxTimeSpan: number,
  startTimeSpan: number,
  interval: number
) => {
  let currentSpan = startTimeSpan

  const _options = [
    {
      label: `${currentSpan} mins`,
      value: currentSpan,
    },
  ]
  while (currentSpan < maxTimeSpan) {
    currentSpan += interval || 0
    _options.push({
      label: `${currentSpan} mins`,
      value: currentSpan,
    })
  }

  return _options
}

function getSlotDurationMinutesFromCalendar(
  calendar: {
    slotDuration?: unknown
    data?: Record<string, unknown>
  } | null | undefined
): number {
  if (!calendar) {
    return 0
  }

  const calendarRecord = calendar as Record<string, unknown>
  const data = calendarRecord.data
  const raw = Number(
    calendarRecord.slotDuration ??
      (data && typeof data === 'object' ? data.slotDuration : undefined) ??
      0
  )

  return normalizeApiMinutes(raw)
}

/** Sorted labels for calendar list / tooltips (e.g. "15 min", "1 hr 30 min"). */
export function getCalendarDurationLabels(
  calendar: {
    slotDuration?: unknown
    durationOptions?: unknown
    data?: Record<string, unknown>
  } | null | undefined
): string[] {
  const rawOptions = getDurationOptionsFromCalendar(calendar)

  if (Array.isArray(rawOptions) && rawOptions.length > 0) {
    return rawOptions
      .map((rawOption, index) => {
        const opt = rawOption as Record<string, unknown>
        const minutes = normalizeApiMinutes(Number(opt.duration ?? 0))
        return {
          order: Number(opt.order ?? index + 1),
          label: formatDuration(minutes),
        }
      })
      .sort((a, b) => a.order - b.order)
      .map((item) => item.label)
      .filter(Boolean)
  }

  const minutes = getSlotDurationMinutesFromCalendar(calendar)
  return minutes > 0 ? [formatDuration(minutes)] : []
}
export const normalizeDurationUnitForApi = (unit: string) =>
  unit === 'minutes' ? 'mins' : unit

export const wait = ms => new Promise(res => setTimeout(res, ms))

export const getInitials = (name: string) => {
  try {
    if (name.trim()) {
      const nameParts = name
        .trim()
        .split(' ')
        .filter(part => part !== '')
      const firstInitial = nameParts[0][0]?.toUpperCase()
      const secondInitial =
        nameParts.length > 1 ? nameParts[1][0]?.toUpperCase() : undefined

      if (secondInitial !== undefined) {
        return firstInitial + secondInitial
      }
      return firstInitial
    }
    return '-'
  } catch (error) {
    return '-'
  }
}
export function cleanHTMLForEmail(text: string) {
  let html = text
  html = html.replace(/<p(\s+[^>]*?)?>\s*(&nbsp;|\s)*<\/p>/g, '<br/>')

  // replace empty list items
  html = html.replace(/<li>(<br\/>)?<\/li>/g, '')

  return html
}

export function formatHTMLForTiptap(text: string) {
  let html = text
  html = html.replace(/<br\/>/gi, '<p style="margin: 0px;"></p>')
  html = html.replace(/\n/g, '')

  return html
}
export function isFileTypeSupported(type: string) {
  if (!type) {
    return true
  }
  if (
    type.includes('html') ||
    type.includes('php') ||
    type.includes('zip') ||
    type.includes('rar')
  ) {
    return false
  }
  return true
}
